import { useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"

/**
 * The cash-register sound for a new order (owners, 15 Oct 2026). A notification's own sound cannot be
 * chosen by a web app (the phone plays its default for every web push), so this plays when HQ is open
 * on the screen: the service worker (public/sw.js) tells the page about each new-order push.
 *
 * The sound is the owners' own recording (15 Oct 2026, replacing one made in code that they did not
 * like): public/sounds/new-order.mp3, 1.4 s, cut from their screen recording. It is fetched and decoded
 * once and kept, so it plays at once. A phone lets a page play sound only after a tap on it, so the
 * first tap anywhere in HQ unlocks it (and loads the sound).
 */
const SOUND_URL = "/sounds/new-order.mp3"
const KEY = "hq-order-sound"

export const orderSoundOn = () => {
  try {
    return localStorage.getItem(KEY) !== "off"
  } catch {
    return true
  }
}
export const setOrderSound = (on: boolean) => {
  try {
    localStorage.setItem(KEY, on ? "on" : "off")
  } catch {
    // Kept for this visit only.
  }
}

type Ctx = AudioContext
let ctx: Ctx | null = null
function context(): Ctx | null {
  if (ctx) return ctx
  const Audio = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Audio) return null
  ctx = new Audio()
  return ctx
}

let sound: Promise<AudioBuffer | null> | null = null

/** The recording, decoded once (null if it cannot be loaded: then nothing plays). */
function loadSound(c: Ctx): Promise<AudioBuffer | null> {
  if (!sound) {
    sound = fetch(SOUND_URL)
      .then((response) => (response.ok ? response.arrayBuffer() : Promise.reject(new Error(String(response.status)))))
      // Older Safari only has the callback form of decodeAudioData.
      .then((data) => new Promise<AudioBuffer>((resolve, reject) => c.decodeAudioData(data, resolve, reject)))
      .catch(() => {
        sound = null
        return null
      })
  }
  return sound
}

/** Called on a tap: lets the page play sound later (iOS needs a sound started inside a tap once). */
export function unlockAudio() {
  const c = context()
  if (!c) return
  if (c.state === "suspended") void c.resume()
  const silent = c.createBufferSource()
  silent.buffer = c.createBuffer(1, 1, 22050)
  silent.connect(c.destination)
  silent.start(0)
  void loadSound(c)
}

export function playChaChing() {
  const c = context()
  if (!c) return
  if (c.state === "suspended") void c.resume()
  void loadSound(c).then((buffer) => {
    if (!buffer) return
    const source = c.createBufferSource()
    source.buffer = buffer
    source.connect(c.destination)
    source.start(c.currentTime + 0.01)
  })
}

/** In the app shell: the first tap unlocks sound; a new-order push while HQ is open plays it and refreshes the numbers. */
export function useNewOrderSound() {
  const queryClient = useQueryClient()
  useEffect(() => {
    const unlock = () => unlockAudio()
    window.addEventListener("pointerdown", unlock, { once: true })
    window.addEventListener("touchend", unlock, { once: true })
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== "hq-new-order") return
      void queryClient.invalidateQueries({ queryKey: ["hq", "orders"] })
      void queryClient.invalidateQueries({ queryKey: ["hq", "pulse"] })
      if (document.visibilityState === "visible" && orderSoundOn()) playChaChing()
    }
    navigator.serviceWorker?.addEventListener("message", onMessage)
    return () => {
      window.removeEventListener("pointerdown", unlock)
      window.removeEventListener("touchend", unlock)
      navigator.serviceWorker?.removeEventListener("message", onMessage)
    }
  }, [queryClient])
}
